<template>
  <div>
    <b-container fluid>
      <b-card no-body>
        <b-overlay
          :show="isLoading"
          rounded="sm"
        >
          <div class="m-2">
            <b-row class="mb-1">
              <b-button
                v-b-modal.modal
                class="ml-1"
                variant="success"
                @click="exportExcel('excel')"
              >
                <span class="text-nowrap text-right">
                  <feather-icon icon="DownloadIcon" /> Xuất Excel
                </span>
              </b-button>
            </b-row>
            <b-row :class="{'mt-0': creatable}">
              <b-col cols="12">
                <div class="d-flex align-items-center mb-1 mt-0">
                  <span class="text-nowrap"> Hiển thị {{ filter.itemsPerPage * (filter.currentPage-1) +1 }}
                    đến {{ (filter.itemsPerPage * (filter.currentPage) >= totalRows)? totalRows : filter.itemsPerPage * (filter.currentPage) }} &nbsp; của {{ totalRows }} bản ghi
                  </span>
                </div>
                <vue-good-table
                  mode="remote"
                  :columns="columns"
                  :rows="accounts"
                  :pagination-options="paginationOptions"
                  :total-rows="totalRows"
                  :line-numbers="true"
                  @on-page-change="onPageChange"
                  @on-sort-change="onSortChange"
                  @on-column-filter="onColumnFilter"
                  @on-per-page-change="onPerPageChange"
                >
                  <div
                    slot="emptystate"
                    style="text-align: center; font-weight: bold"
                  >
                    Không có bản ghi nào !
                  </div>
                  <template
                    slot="table-row"
                    slot-scope="props"
                  >
                    <span v-if="props.column.field === 'status'">
                      {{ getStatusName(props.row.status) }}
                    </span>
                    <span v-else-if="props.column.field === 'rolesNames'">
                      <b-badge
                        v-for="(item, index) in props.row.rolesNames"
                        :key="index"
                        style="margin-left: 0.5rem"
                        variant="primary"
                      >
                        {{ item }}
                      </b-badge>
                    </span>

                    <span v-else-if="props.column.field === 'action'">
                      <b-dropdown
                        variant="link"
                        no-caret
                        :right="$store.state.appConfig.isRTL"
                      >
                        <template #button-content>
                          <feather-icon
                            icon="MoreVerticalIcon"
                            size="16"
                            class="align-middle text-body"
                          />
                        </template>
                        <b-dropdown-item
                          v-if="updatable"
                          v-b-modal.accountSaveModal
                          @click="onEditAccount(props.row)"
                        >
                          <feather-icon icon="EditIcon" />
                          <span class="align-middle ml-50">Sửa</span>
                        </b-dropdown-item>

                        <b-dropdown-item
                          v-if="accountRoleReadable"
                          @click="onAssignRole(props.row.id)"
                        >
                          <feather-icon icon="TargetIcon" />
                          <span class="align-middle ml-50">Phân vai trò</span>
                        </b-dropdown-item>

                        <b-dropdown-item
                          v-if="creatable"
                          @click="onResetPassword(props.row.id)"
                        >
                          <feather-icon icon="RotateCcwIcon" />
                          <span class="align-middle ml-50">Đặt lại mật khẩu</span>
                        </b-dropdown-item>

                        <b-dropdown-item
                          v-if="(creatable || updatable) && !!props.row.linkAccountId"
                          @click="onUnlinkAccount(props.row)"
                        >
                          <feather-icon icon="CloudOffIcon" />
                          <span class="align-middle ml-50">Hủy liên kết tài khoản</span>
                        </b-dropdown-item>

                        <b-dropdown-item
                          v-if="deletable"
                          @click="onDeleteAccount(props.row)"
                        >
                          <feather-icon icon="TrashIcon" />
                          <span class="align-middle ml-50">Xóa</span>
                        </b-dropdown-item>
                      </b-dropdown>
                    </span>

                    <!-- Column: Common -->
                    <span v-else>{{ props.formattedRow[props.column.field] }}</span>
                  </template>

                  <!-- pagination -->
                  <template
                    slot="pagination-bottom"
                    slot-scope="props"
                  >
                    <div class="d-flex justify-content-between flex-wrap">
                      <div class="d-flex align-items-center mb-0 mt-1">
                        <span class="text-nowrap"> Hiển thị {{ filter.itemsPerPage * (filter.currentPage-1) +1 }}
                          đến {{ (filter.itemsPerPage * (filter.currentPage) >= totalRows)? totalRows : filter.itemsPerPage * (filter.currentPage) }} &nbsp; của {{ totalRows }} bản ghi
                        </span>
                      </div>
                      <div class="d-flex align-items-center mb-0 mt-1">
                        <span class="text-nowrap"> Hiển thị </span>
                        <b-form-select
                          v-model="filter.itemsPerPage"
                          :options="itemsPerPageOptions"
                          class="mx-1"
                          @input="(value) => props.perPageChanged({ currentPerPage: value })"
                        />
                        <span class="text-nowrap"> bản ghi/trang</span>
                      </div>
                      <div>
                        <b-pagination
                          :value="1"
                          :total-rows="totalRows"
                          :per-page="filter.itemsPerPage"
                          class="mt-1 mb-0"
                          @input="(value) => props.pageChanged({ currentPage: value })"
                        />
                      </div>
                    </div>
                  </template>
                </vue-good-table>
              </b-col>
            </b-row>
          </div>
        </b-overlay>
      </b-card>
    </b-container>
    <AccountSave
      :account="currentAccount"
      @succeed="onSucceed"
    />
    <ResetPassword :id="selectedId" />
    <AssignRole
      :id="selectedId"
      @succeed="onSucceed"
    />
  </div>
</template>

<script>
import Ripple from 'vue-ripple-directive'
import { VueGoodTable } from 'vue-good-table'
import '@core/scss/vue/libs/vue-good-table.scss'
import AccountSave from '@/views/authorization/account/AccountSave.vue'
import { mapActions, mapGetters } from 'vuex'
import { STATUSES } from '@/const/status'
import {
  BButton, BCard, BCol, BContainer, BFormSelect, BOverlay, BPagination, BRow, BDropdown, BDropdownItem, BBadge,
} from 'bootstrap-vue'
import ResetPassword from '@/views/authorization/account/ResetPassword.vue'
import AssignRole from '@/views/authorization/account/AssignRole.vue'
import { hasPermissionForResource } from '@/utils'
import { PermissionCode, ResourceCode } from '@/const/code'
import { showErrorToast, showSuccessToast } from '@/utils/notification'
import * as XLSX from 'xlsx'
import * as accountApi from './api'

export default {
  name: 'Account',
  directives: {
    Ripple,
  },
  components: {
    AccountSave,
    ResetPassword,
    AssignRole,
    VueGoodTable,
    BButton,
    BCard,
    BCol,
    BContainer,
    BFormSelect,
    BOverlay,
    BPagination,
    BRow,
    BDropdown,
    BDropdownItem,
    BBadge,
  },
  data() {
    return {
      isLoading: false,
      accounts: [],
      totalRows: 0,
      filter: {
        currentPage: 1,
        itemsPerPage: 10,
        username: '',
        status: null,
        notInUsername: 'SystemAdmin',
        type: null,
        roleId: null,
        sort: '',
      },
      filterExport: {
        type: null,
      },
      currentAccount: undefined,
      paginationOptions: {
        enabled: true,
      },
      itemsPerPageOptions: [10, 20, 30, 50, 80, 100],
      selectedId: undefined,
    }
  },
  computed: {
    ...mapGetters({
      roles: 'role/roles',
    }),
    creatable() {
      return hasPermissionForResource(PermissionCode.CREATE, ResourceCode.ACCOUNT)
    },
    updatable() {
      return hasPermissionForResource(PermissionCode.UPDATE, ResourceCode.ACCOUNT)
    },
    deletable() {
      return hasPermissionForResource(PermissionCode.DELETE, ResourceCode.ACCOUNT)
    },
    accountRoleReadable() {
      return hasPermissionForResource(PermissionCode.READ, ResourceCode.ACCOUNT_ROLE)
    },
    columns() {
      return [
        {
          label: 'Tên người dùng',
          field: 'name',
          filterOptions: {
            enabled: true,
            trigger: 'enter',
            placeholder: 'Tên người dùng',
          },
          sortable: false,
          thClass: 'text-center',
          tdClass: 'text-left',
        },
        {
          label: 'Tài khoản',
          field: 'username',
          filterOptions: {
            enabled: true,
            trigger: 'enter',
            placeholder: 'Tên tài khoản',
          },
          sortable: true,
          thClass: 'text-center',
        },
        {
          label: 'Vai trò',
          field: 'rolesNames',
          filterOptions: {
            enabled: true,
            multiple: true,
            filterDropdownItems: this.roles.map(item => ({ value: item.id, text: item.name })),
            placeholder: 'Tất cả',
          },
          sortable: true,
          thClass: 'text-center',
        },
        {
          label: 'Trạng thái',
          field: 'status',
          filterOptions: {
            enabled: true,
            filterDropdownItems: STATUSES.map(status => ({ value: status.value, text: status.label })),
            placeholder: 'Tất cả',
          },
          sortable: false,
          thClass: 'text-center',
          tdClass: 'text-center',
        },
        {
          label: 'Phân loại',
          field: 'typeName',
          filterOptions: {
            enabled: true,
            filterDropdownItems: [{ value: 1, text: 'Cán bộ' }, { value: 2, text: 'Sinh viên' }],
            placeholder: 'Tất cả',
          },
          sortable: false,
          thClass: 'text-center',
        },
        {
          label: '',
          field: 'action',
          sortable: false,
          tdClass: 'text-center',
        },
      ]
    },
  },
  async created() {
    await Promise.all([
      this.getAccountsFromApi(),
      this.getAllRoles(),
    ])
  },
  methods: {
    ...mapActions({
      getAllRoles: 'role/getAllRoles',
    }),
    onEditAccount(account) {
      const {
        rowNum,
        originalIndex,
        vgt_id,
        ...rest
      } = account
      this.currentAccount = rest
    },
    async exportExcel() {
      this.isLoading = true
      try {
        const params = {
          currentPage: 1,
          itemsPerPage: 10000000,
          username: this.filter.username || '',
          status: this.filter.status,
          notInUsername: 'SystemAdmin',
          type: this.filter.type,
          roleId: this.filter.roleId,
          sort: this.filter.sort,
          name: this.filter.name || '',
        }

        const allAccounts = await accountApi.getAllAccounts(params)

        if (!allAccounts || allAccounts.length === 0) {
          showErrorToast(this, 'Không có dữ liệu để xuất!')
          return
        }

        // Chuẩn bị dữ liệu cho excel
        const excelData = allAccounts.map((account, index) => ({
          'STT': index + 1,
          'Tên người dùng': account.name || '',
          'Tài khoản': account.username || '',
          'Vai trò': Array.isArray(account.rolesNames) ? account.rolesNames.join(', ') : '',
          'Trạng thái': this.getStatusName(account.status),
          'Phân loại': account.typeName || '',
        }))

        // Tạo workbook và worksheet
        const wb = XLSX.utils.book_new()
        const ws = XLSX.utils.json_to_sheet(excelData)

        // Tự động điều chỉnh độ rộng cột
        const colWidths = [
          { wch: 5 },  // STT
          { wch: 30 }, // Tên người dùng
          { wch: 20 }, // Tài khoản
          { wch: 30 }, // Vai trò
          { wch: 15 }, // Trạng thái
          { wch: 15 }, // Phân loại
        ]
        ws['!cols'] = colWidths

        // Thêm worksheet vào workbook
        XLSX.utils.book_append_sheet(wb, ws, 'Danh sách tài khoản')

        // Xuất file excel
        const fileName = `danh_sach_tai_khoan_${new Date().getTime()}.xlsx`
        XLSX.writeFile(wb, fileName)

        showSuccessToast(this, 'Xuất Excel thành công!')
      } catch (e) {
        showErrorToast(this, 'Có lỗi xảy ra khi xuất Excel!')
        console.error(e)
      } finally {
        this.isLoading = false
      }
    },
    async onDeleteAccount(account) {
      this.$swal({
        title: `Bạn chắc chắn muốn xóa tài khoản <span class="text-danger">${account.username}</span>?`,
        text: 'Bạn sẽ không thể khôi phục lại dữ liệu này!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: '<i class="fa-solid fa-check"></i> Đồng ý',
        cancelButtonText: '<i class="fa-solid fa-times"></i> Hủy',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      })
        .then(async result => {
          if (result.value) {
            this.isLoading = true
            try {
              const response = await accountApi.deleteAccount(account.id)
              if (response) {
                const { isSuccessful, message } = response
                if (isSuccessful) {
                  showSuccessToast(this, message)
                  await this.getAccountsFromApi()
                } else {
                  showErrorToast(this, message)
                }
              }
            } catch (e) {
              showErrorToast(this, e)
            } finally {
              this.isLoading = false
            }
          }
        })
    },
    updateParams(newProps) {
      this.filter = { ...this.filter, ...newProps }
    },
    async onPageChange(param) {
      this.updateParams({ currentPage: param.currentPage })
      await this.getAccountsFromApi()
    },
    async getAccountsFromApi() {
      this.isLoading = true
      try {
        const { records, total } = await accountApi.getAccounts(this.filter)
        this.accounts = records
        this.totalRows = total
      } catch (e) {
        showErrorToast(this, e)
      } finally {
        this.isLoading = false
      }
    },
    async onPerPageChange(param) {
      this.updateParams({ currentPage: 1, itemsPerPage: param.currentPerPage })
      await this.getAccountsFromApi()
    },
    async onSortChange(param) {
      if (param.length > 0) {
        const {
          field,
          type,
        } = param[0]
        if (field === 'username') this.updateParams({ sort: `1_${type}` })
        else this.updateParams({ sort: `2_${type}` })
      }
      await this.getAccountsFromApi()
    },
    async onColumnFilter(param) {
      const { columnFilters } = param
      if (Object.hasOwn(columnFilters, 'name')) this.updateParams({ name: columnFilters.name })
      if (Object.hasOwn(columnFilters, 'username')) this.updateParams({ username: columnFilters.username })
      if (Object.hasOwn(columnFilters, 'status')) this.updateParams({ status: columnFilters.status })
      if (Object.hasOwn(columnFilters, 'typeName')) this.updateParams({ type: columnFilters.typeName })
      if (Object.hasOwn(columnFilters, 'rolesNames')) this.updateParams({ roleId: columnFilters.rolesNames })
      await this.getAccountsFromApi()
    },
    getStatusName(id) {
      return STATUSES.find(status => status.value === id).label
    },
    async onSucceed() {
      await this.getAccountsFromApi()
    },
    onResetPassword(id) {
      this.openModal(id, 'resetPasswordModal')
    },
    onAssignRole(id) {
      this.openModal(id, 'assignRoleModal')
    },
    openModal(accountId, modalId) {
      if (!accountId) {
        showErrorToast(this, 'Không tìm được tài khoản tương ứng')
      } else {
        this.selectedId = accountId
        this.$bvModal.show(modalId)
      }
    },
    async onUnlinkAccount(account) {
      this.$swal({
        title: `Bạn chắc chắn muốn hủy liên kết tài khoản <span class="text-danger">${account.name}</span>?`,
        text: 'Bạn sẽ không thể khôi phục lại dữ liệu này!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: '<i class="fa-solid fa-check"></i> Đồng ý',
        cancelButtonText: '<i class="fa-solid fa-times"></i> Hủy',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      })
        .then(async result => {
          if (result.value) {
            this.isLoading = true
            try {
              const response = await accountApi.unlinkAccount(account.id)
              if (response) {
                const { isSuccessful, message } = response
                if (isSuccessful) {
                  showSuccessToast(this, message)
                  await this.getAccountsFromApi()
                } else {
                  showErrorToast(this, message)
                }
              }
            } catch (e) {
              showErrorToast(this, e)
            } finally {
              this.isLoading = false
            }
          }
        })
    },
  },
}
</script>
<style lang="scss">
.dropdown-menu.show {
  height: 14rem !important;
  overflow-y: auto !important;
}
</style>
